import React, { useState } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Avatar,
  Badge,
  Divider,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  SimpleGrid,
  Card,
  CardBody,
  CardHeader,
  Heading,
  IconButton,
  Tooltip,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  Input,
  FormControl,
  FormLabel,
  Icon
} from '@chakra-ui/react';
import { 
  CopyIcon, 
  ExternalLinkIcon, 
  RepeatIcon, 
  EditIcon,
  CheckIcon,
  CloseIcon
} from '@chakra-ui/icons';
import { FaWallet } from 'react-icons/fa';
import useWallet, { WALLET_STATUS } from '../store/useWallet.js';
import useAuth from '../store/useAuth.js'; // Add auth hook for user wallet info
// import { updateWalletInfo } from '../lib/database.js'; // Removed in wallet-only migration

/**
 * Wallet Info Component
 * Displays comprehensive wallet information and management options
 */
const WalletInfo = ({ 
  showHeader = true,
  showStats = true,
  showActions = true,
  compact = false,
  showBalance = true,
  showAdmin = true,
  ...props 
}) => {
  const {
    status,
    address,
    xrpBalance,
    xgcBalance,
    disconnect,
    fetchBalances
  } = useWallet();

  const { isLoggedIn, user } = useAuth();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [isEditing, setIsEditing] = useState(false);
  const [newDisplayName, setNewDisplayName] = useState('');
  const [isUpdating, setIsUpdating] = useState(false);
  const toast = useToast();

  // Computed values - Check both direct wallet connection and user auth wallet
  const isDirectWalletConnected = status === WALLET_STATUS.CONNECTED && address;
  const isUserWalletLinked = isLoggedIn && user?.walletAddress;
  const isConnected = isDirectWalletConnected || isUserWalletLinked;
  
  // Use direct wallet address if connected, otherwise use user's stored wallet address
  const walletAddress = address || user?.walletAddress;
  const truncatedAddress = walletAddress ? `${walletAddress.substring(0, 6)}...${walletAddress.substring(walletAddress.length - 4)}` : '';
  const hasRequiredTokens = xgcBalance > 0;
  const isAdmin = xgcBalance >= 1000; // You can adjust this threshold
  const displayName = ''; // For now, we don't have display names in the store

  /**
   * Copy wallet address to clipboard
   */
  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(walletAddress);
      toast({
        title: 'Address Copied',
        description: 'Wallet address copied to clipboard',
        status: 'success',
        duration: 2000,
        isClosable: true
      });
    } catch (error) {
      toast({
        title: 'Copy Failed',
        description: 'Failed to copy address to clipboard',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
    }
  };

  /**
   * Open wallet in XRPL explorer
   */
  const openInExplorer = () => {
    const explorerUrl = process.env.REACT_APP_XRPL_NETWORK?.includes('mainnet') 
      ? `https://livenet.xrpl.org/accounts/${walletAddress}`
      : `https://testnet.xrpl.org/accounts/${walletAddress}`;
    
    window.open(explorerUrl, '_blank');
  };

  /**
   * Handle balance refresh
   */
  const handleRefreshBalance = async () => {
    try {
      await fetchBalances();
      toast({
        title: 'Balance Updated',
        description: 'Your balances have been refreshed',
        status: 'success',
        duration: 3000,
        isClosable: true
      });
    } catch (error) {
      toast({
        title: 'Refresh Failed',
        description: 'Failed to refresh balances',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
    }
  };

  /**
   * Handle display name update
   */
  const handleUpdateDisplayName = async () => {
    if (!newDisplayName.trim()) {
      toast({
        title: 'Invalid Name',
        description: 'Display name cannot be empty',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
      return;
    }

    setIsUpdating(true);
    try {
      // Display name updates removed in wallet-only migration
      // All profile updates now handled via API
      console.log('Display name update requested:', newDisplayName.trim());
      
      // Simulate successful update for now
      // TODO: Implement via REST API when needed

      toast({
        title: 'Profile Updated',
        description: 'Your display name has been updated',
        status: 'success',
        duration: 3000,
        isClosable: true
      });

      setIsEditing(false);
    } catch (error) {
      toast({
        title: 'Update Failed',
        description: error.message || 'Failed to update profile',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
    } finally {
      setIsUpdating(false);
    }
  };

  /**
   * Cancel editing
   */
  const cancelEditing = () => {
    setNewDisplayName('');
    setIsEditing(false);
  };

  /**
   * Format last balance check time
   */
  const formatLastCheck = () => {
    return 'Recently'; // Simplified for now
  };

  /**
   * Get network badge color
   */
  const getNetworkBadgeColor = () => {
    return process.env.REACT_APP_XRPL_NETWORK?.includes('mainnet') ? 'green' : 'orange';
  };

  /**
   * Get network name
   */
  const getNetworkName = () => {
    return process.env.REACT_APP_XRPL_NETWORK?.includes('mainnet') ? 'Mainnet' : 'Testnet';
  };

  if (!isConnected) {
    return (
      <Box textAlign="center" py={8} {...props}>
        <Text color="gray.500">No wallet connected</Text>
      </Box>
    );
  }

  if (compact) {
    return (
      <Box 
        px={3}
        py={2}
        bg="rgba(0, 0, 0, 0.3)"
        borderRadius="md"
        borderWidth="1px"
        borderColor="rgba(0, 255, 127, 0.3)"
        boxShadow="0 0 10px rgba(0, 255, 127, 0.15)"
        {...props}
      >
        <HStack spacing={2}>
          <Icon as={FaWallet} color="rgb(0, 255, 127)" />
          <VStack spacing={0} align="start">
            <Text color="white" fontSize="sm" fontWeight="medium">
              {truncatedAddress}
            </Text>
            {isDirectWalletConnected ? (
              <HStack spacing={1}>
                {/* <Text color="green.300" fontSize="xs">
                  {xgcBalance.toLocaleString()} XGC
                </Text>
                <Text color="gray.400" fontSize="xs">
                  ({xrpBalance.toFixed(2)} XRP)
                </Text> */}
              </HStack>
            ) : (
              <Text color="green.300" fontSize="xs">
                Connected
              </Text>
            )}
          </VStack>
          {isAdmin && <Badge colorScheme="purple" size="sm">Admin</Badge>}
        </HStack>
      </Box>
    );
  }

  return (
    <Box {...props}>
      <Card>
        {showHeader && (
          <CardHeader>
            <HStack justify="space-between">
              <Heading size="md">Wallet Information</Heading>
              <HStack>
                <Badge colorScheme={getNetworkBadgeColor()}>
                  {getNetworkName()}
                </Badge>
                {isAdmin && (
                  <Badge colorScheme="purple">Admin</Badge>
                )}
              </HStack>
            </HStack>
          </CardHeader>
        )}

        <CardBody>
          <VStack spacing={6} align="stretch">
            {/* Profile Section */}
            <VStack spacing={3}>
              <Avatar 
                size="lg" 
                name={displayName || truncatedAddress}
                bg="blue.500"
              />
              
              {isEditing ? (
                <HStack spacing={2}>
                  <Input
                    value={newDisplayName}
                    onChange={(e) => setNewDisplayName(e.target.value)}
                    placeholder="Enter display name"
                    size="sm"
                    maxW="200px"
                  />
                  <IconButton
                    size="sm"
                    icon={<CheckIcon />}
                    colorScheme="green"
                    onClick={handleUpdateDisplayName}
                    isLoading={isUpdating}
                  />
                  <IconButton
                    size="sm"
                    icon={<CloseIcon />}
                    onClick={cancelEditing}
                  />
                </HStack>
              ) : (
                <HStack spacing={2}>
                  <Text fontSize="lg" fontWeight="medium">
                    {displayName || 'Unnamed Wallet'}
                  </Text>
                  <Tooltip label="Edit Display Name">
                    <IconButton
                      size="sm"
                      icon={<EditIcon />}
                      variant="ghost"
                      onClick={() => setIsEditing(true)}
                    />
                  </Tooltip>
                </HStack>
              )}

              <HStack spacing={2}>
                <Text fontSize="sm" fontFamily="mono" color="gray.600">
                  {truncatedAddress}
                </Text>
                <Tooltip label="Copy Address">
                  <IconButton
                    size="xs"
                    icon={<CopyIcon />}
                    variant="ghost"
                    onClick={copyAddress}
                  />
                </Tooltip>
                <Tooltip label="View in Explorer">
                  <IconButton
                    size="xs"
                    icon={<ExternalLinkIcon />}
                    variant="ghost"
                    onClick={openInExplorer}
                  />
                </Tooltip>
              </HStack>
            </VStack>

            <Divider />

            {/* Balance Stats */}
            {showStats && (
              <SimpleGrid columns={2} spacing={4}>
                <Stat>
                  <StatLabel>XRP Balance</StatLabel>
                  <StatNumber>{xrpBalance.toFixed(2)}</StatNumber>
                  <StatHelpText>XRP</StatHelpText>
                </Stat>

                <Stat>
                  <StatLabel>XGC Balance</StatLabel>
                  <StatNumber color={hasRequiredTokens ? 'green.500' : 'red.500'}>
                    {xgcBalance.toLocaleString()}
                  </StatNumber>
                  <StatHelpText>
                    {hasRequiredTokens ? (
                      <HStack spacing={1}>
                        <StatArrow type="increase" />
                        <Text>Sufficient</Text>
                      </HStack>
                    ) : (
                      <HStack spacing={1}>
                        <StatArrow type="decrease" />
                        <Text>Insufficient</Text>
                      </HStack>
                    )}
                  </StatHelpText>
                </Stat>
              </SimpleGrid>
            )}

            {/* Last Update Info */}
            <HStack justify="space-between" fontSize="sm" color="gray.500">
              <Text>Last updated: {formatLastCheck()}</Text>
              <Tooltip label="Refresh Balances">
                <IconButton
                  size="sm"
                  icon={<RepeatIcon />}
                  variant="ghost"
                  onClick={handleRefreshBalance}
                />
              </Tooltip>
            </HStack>

            {/* Actions */}
            {showActions && (
              <>
                <Divider />
                <HStack spacing={3}>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={onOpen}
                  >
                    Full Address
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    colorScheme="red"
                    onClick={disconnect}
                  >
                    Disconnect
                  </Button>
                </HStack>
              </>
            )}
          </VStack>
        </CardBody>
      </Card>

      {/* Full Address Modal */}
      <Modal isOpen={isOpen} onClose={onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Full Wallet Address</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl>
              <FormLabel>Wallet Address</FormLabel>
              <HStack>
                <Input
                  value={address}
                  isReadOnly
                  fontFamily="mono"
                  fontSize="sm"
                />
                <IconButton
                  icon={<CopyIcon />}
                  onClick={copyAddress}
                  aria-label="Copy address"
                />
              </HStack>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default WalletInfo; 